class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);
    this.onFacetDisclosureToggle = this.onFacetDisclosureToggle.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);

    this.bindFacetDisclosureEvents();
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true, focusTarget = null, drawerScrollTop = null) {
    FacetFiltersForm.pendingDrawerScrollTop = drawerScrollTop;
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner',
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event, focusTarget)
        : FacetFiltersForm.renderSectionFromFetch(url, event, focusTarget);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event, focusTarget) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event, focusTarget);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        FacetFiltersForm.initializeProductGridAnimations();
        FacetFiltersForm.restoreDrawerBodyScroll();
      });
  }

  static renderSectionFromCache(filterDataUrl, event, focusTarget) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event, focusTarget);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    FacetFiltersForm.initializeProductGridAnimations();
    FacetFiltersForm.restoreDrawerBodyScroll();
  }

  static initializeProductGridAnimations() {
    if (typeof initializeScrollAnimationTrigger !== 'function') return;

    const productGrid = document.getElementById('ProductGridContainer');
    if (productGrid) initializeScrollAnimationTrigger(productGrid);
  }

  static restoreDrawerBodyScroll() {
    const scrollTop = FacetFiltersForm.pendingDrawerScrollTop;
    FacetFiltersForm.pendingDrawerScrollTop = null;
    if (scrollTop == null) return;

    const body = document.querySelector('.facet-drawer__body');
    if (!body) return;

    body.scrollTop = scrollTop;
    requestAnimationFrame(() => {
      body.scrollTop = scrollTop;
    });
  }

  static shouldSkipDrawerFilterRender(event) {
    if (!event?.target?.closest('facet-drawer-filters-form')) return false;
    if (event.target.closest('facet-remove a, .facet-drawer__clear')) return false;
    return true;
  }

  static focusWithoutScroll(element) {
    if (!element) return;

    try {
      element.focus({ preventScroll: true });
    } catch {
      element.focus();
    }
  }

  static renderProductGridContainer(html) {
    const container = document.getElementById('ProductGridContainer');
    container.innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    container.querySelectorAll('.scroll-trigger').forEach((element) => {
      element.classList.add('scroll-trigger--cancel');
    });

    container.querySelectorAll('[data-product-list]').forEach((list) => window.resetProductListTracking?.(list));

    window.sortProductsByAvailability?.refresh();
  }

  static renderProductCount(html) {
    const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount').innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner',
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event, focusTarget = null) {
    if (FacetFiltersForm.shouldSkipDrawerFilterRender(event)) return;

    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter',
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter',
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);
    FacetFiltersForm.bindFacetDisclosureEvents();
    FacetFiltersForm.restoreFocus(focusTarget);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        const focusTarget = FacetFiltersForm.getFocusTarget(event.target);

        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';
        const matchingFocusTarget = FacetFiltersForm.findFocusTarget(newFacetDetailsElement, focusTarget);

        if (matchingFocusTarget) {
          FacetFiltersForm.focusWithoutScroll(matchingFocusTarget);
        } else if (newElementToActivate && !isTextInput && focusTarget?.type !== 'checkbox') {
          FacetFiltersForm.focusWithoutScroll(newElementToActivate);
        }
      }
    }
  }

  static getFocusTarget(target) {
    if (!target || target.tagName !== 'INPUT') return null;

    return {
      id: target.id,
      name: target.name,
      type: target.type,
      value: target.value,
    };
  }

  static findFocusTarget(container, focusTarget) {
    if (!container || !focusTarget || focusTarget.type !== 'checkbox') return null;

    if (focusTarget.id) {
      const matchingElement = document.getElementById(focusTarget.id);
      if (matchingElement && container.contains(matchingElement)) return matchingElement;
    }

    return Array.from(container.querySelectorAll('input[type="checkbox"]')).find(
      (input) => input.name === focusTarget.name && input.value === focusTarget.value,
    );
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSortFocusTarget(target) {
    if (!target || target.tagName !== 'SELECT' || target.name !== 'sort_by') return null;

    const form = target.closest('form');
    if (!form?.id) return null;

    return {
      type: 'sort-select',
      selector: `#${form.id} select[name="sort_by"]`,
    };
  }

  static getActiveFacetFocusTarget(trigger) {
    const activeFacetsContainer = trigger.closest('.active-facets-mobile, .active-facets-desktop');
    if (!activeFacetsContainer) return null;

    const activeFacetButtons = Array.from(
      activeFacetsContainer.querySelectorAll('.active-facets__button[role="button"]'),
    );

    return {
      type: 'active-facet',
      activeFacetContainerSelector: activeFacetsContainer.classList.contains('active-facets-mobile')
        ? '.active-facets-mobile'
        : '.active-facets-desktop',
      activeFacetIndex: activeFacetButtons.indexOf(trigger),
      isClearAll: trigger.classList.contains('active-facets__button-remove'),
    };
  }

  static restoreFocus(focusTarget) {
    if (!focusTarget?.type) return;

    if (focusTarget.type === 'sort-select') {
      FacetFiltersForm.focusWithoutScroll(document.querySelector(focusTarget.selector));
      return;
    }

    if (focusTarget.type === 'active-facet') {
      FacetFiltersForm.restoreActiveFacetFocus(focusTarget);
    }
  }

  static restoreActiveFacetFocus(focusTarget) {
    if (!focusTarget?.activeFacetContainerSelector) return;

    const activeFacetsContainer = document.querySelector(focusTarget.activeFacetContainerSelector);
    const activeFacetButtons = activeFacetsContainer
      ? Array.from(activeFacetsContainer.querySelectorAll('.active-facets__button[role="button"]'))
      : [];

    if (!focusTarget.isClearAll && activeFacetButtons.length > 0) {
      const nextActiveFacetButton =
        activeFacetButtons[focusTarget.activeFacetIndex] || activeFacetButtons[activeFacetButtons.length - 1];

      FacetFiltersForm.focusWithoutScroll(nextActiveFacetButton);
      return;
    }

    const fallbackSummarySelector =
      focusTarget.activeFacetContainerSelector === '.active-facets-mobile'
        ? '.mobile-facets__wrapper summary[role="button"]'
        : '#FacetFiltersForm .facets__summary[role="button"]';

    FacetFiltersForm.focusWithoutScroll(document.querySelector(fallbackSummarySelector));
  }

  static bindFacetDisclosureEvents() {
    document.querySelectorAll('facet-filters-form').forEach((facetFiltersForm) => {
      facetFiltersForm.bindFacetDisclosureEvents();
    });
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    const focusTarget = FacetFiltersForm.getSortFocusTarget(event?.target);
    FacetFiltersForm.renderPage(searchParams, event, true, focusTarget);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const focusTarget = FacetFiltersForm.getActiveFacetFocusTarget(event.currentTarget);
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url, null, true, focusTarget);
  }

  bindFacetDisclosureEvents() {
    this.querySelectorAll('.facets__disclosure').forEach((disclosure) => {
      if (disclosure.dataset.focusTrapBound === 'true') return;

      disclosure.dataset.focusTrapBound = 'true';
      disclosure.addEventListener('toggle', this.onFacetDisclosureToggle);
    });
  }

  onFacetDisclosureToggle(event) {
    const disclosure = event.currentTarget;
    const summary = disclosure.querySelector('.facets__summary');

    if (!summary) return;

    if (disclosure.hasAttribute('open')) {
      trapFocus(disclosure, summary);
      return;
    }

    removeTrapFocus(summary);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
FacetFiltersForm.pendingDrawerScrollTop = null;
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);
